import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogClose, DialogTitle } from 'components/ui/Dialog';
import { downloadAgentsCsvTemplate, parseAgentsCsv, readFileText, } from 'features/agents/csv';
import { useUpsertAgentWithEmail } from 'features/agents/useUpsertAgentWithEmail';
import { useAuthStatus } from 'features/auth';
function Spinner() {
    return (_jsxs("svg", { "aria-hidden": "true", width: "16", height: "16", viewBox: "0 0 24 24", className: "animate-spin text-current", children: [_jsx("circle", { cx: "12", cy: "12", r: "9", fill: "none", stroke: "currentColor", strokeWidth: "2", className: "opacity-25" }), _jsx("path", { d: "M21 12a9 9 0 0 0-9-9", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" })] }));
}
function DownloadIcon() {
    return (_jsxs("svg", { "aria-hidden": "true", width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", children: [_jsx("path", { d: "M10 3v9m0 0l-3.5-3.5M10 12l3.5-3.5", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M3.5 14.5V16a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-1.5", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round" })] }));
}
function ImportIcon() {
    return (_jsxs("svg", { "aria-hidden": "true", width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", children: [_jsx("path", { d: "M10 12V3m0 9l3.5-3.5M10 12L6.5 8.5", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M3.5 14.5V16a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-1.5", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round" })] }));
}
export default function ImportCsvDialog({ open, onOpenChange }) {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const [state, setState] = useState({ kind: 'idle' });
    const upsert = useUpsertAgentWithEmail();
    const queryClient = useQueryClient();
    const authStatus = useAuthStatus();
    const userEmail = authStatus.kind === 'authenticated' ? authStatus.user.email : undefined;
    const isBusy = state.kind === 'previewing' || state.kind === 'committing';
    const previewedRows = state.kind === 'preview-success' ? state.rows
        : state.kind === 'commit-error' ? state.rows
            : null;
    const validCount = state.kind === 'preview-success' ? state.rows.length
        : state.kind === 'committing' ? state.total
            : state.kind === 'commit-error' ? state.rows.length
                : 0;
    const canCommit = previewedRows !== null && validCount > 0 && !isBusy;
    const handlePickFile = () => {
        if (isBusy)
            return;
        fileInputRef.current?.click();
    };
    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file)
            return;
        setState({ kind: 'previewing', filename: file.name });
        try {
            const text = file.size === 0 ? '' : await readFileText(file);
            const parsed = parseAgentsCsv(text);
            // Scenario 6: file is structurally broken.
            if (parsed.formatError) {
                setState({
                    kind: 'preview-error',
                    filename: file.name,
                    error: {
                        kind: 'bad-format',
                        messageKey: parsed.formatError === 'empty'
                            ? 'import_csv_error_empty_file'
                            : 'import_csv_error_bad_format',
                    },
                });
                return;
            }
            // Scenario 7: parsed cleanly, but no row passed validation.
            if (parsed.validRows.length === 0) {
                setState({
                    kind: 'preview-error',
                    filename: file.name,
                    error: { kind: 'no-valid-rows' },
                });
                return;
            }
            setState({
                kind: 'preview-success',
                filename: file.name,
                rows: parsed.validRows,
                invalidCount: parsed.invalidRows.length,
            });
        }
        catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            setState({
                kind: 'preview-error',
                filename: file.name,
                error: { kind: 'read-failed', detail: message },
            });
        }
    };
    const handleCommit = async () => {
        if (!canCommit || !previewedRows)
            return;
        const filename = state.kind === 'preview-success' || state.kind === 'commit-error' ? state.filename : '';
        setState({ kind: 'committing', filename, total: previewedRows.length });
        try {
            await Promise.all(previewedRows.map((row) => upsert.mutateAsync(row)));
            void queryClient.invalidateQueries({ queryKey: ['listAgents', userEmail] });
            setState({ kind: 'idle' });
            onOpenChange(false);
        }
        catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            setState({
                kind: 'commit-error',
                filename,
                rows: previewedRows,
                message,
            });
            // Some rows may have succeeded even though the batch failed — refresh the list.
            void queryClient.invalidateQueries({ queryKey: ['listAgents', userEmail] });
        }
    };
    const handleOpenChange = (next) => {
        if (!next)
            setState({ kind: 'idle' });
        onOpenChange(next);
    };
    const submitLabel = canCommit
        ? t('import_csv_submit_count', { count: validCount })
        : state.kind === 'committing'
            ? t('import_csv_committing')
            : t('import_csv_submit_default');
    return (_jsx(Dialog, { open: open, onOpenChange: handleOpenChange, ariaLabel: t('agent_add_title'), maxWidthClassName: "max-w-md md:max-w-[560px]", children: _jsxs("div", { className: "flex flex-col items-center gap-4 p-4", style: { background: 'linear-gradient(0deg, rgba(53,98,232,0.05), rgba(53,98,232,0.05)), #ffffff' }, children: [_jsx(DialogTitle, { className: "m-0 w-full text-[20px] font-semibold leading-[1.1] text-blacktext", children: t('agent_add_title') }), _jsxs("div", { className: "flex w-full flex-col gap-4 rounded border border-primary-070 bg-white p-4", children: [_jsx("div", { className: "flex w-full items-center justify-between", children: _jsx("p", { className: "m-0 text-[16px] font-semibold leading-normal text-blacktext", children: t('import_csv_section_label') }) }), _jsxs("div", { className: "flex w-full flex-col gap-2 md:flex-row", children: [_jsxs("button", { type: "button", onClick: () => downloadAgentsCsvTemplate(), disabled: isBusy, className: `inline-flex h-10 w-full items-center justify-center gap-1 rounded border border-primary bg-transparent px-6 py-[11px] text-[14px] font-medium leading-6 text-primary ${isBusy ? 'cursor-not-allowed opacity-60' : ''}`, children: [_jsx("span", { children: t('import_csv_download_template') }), _jsx(DownloadIcon, {})] }), _jsx("button", { type: "button", onClick: handlePickFile, disabled: isBusy, className: `inline-flex h-10 w-full items-center justify-center gap-1 rounded border border-primary bg-transparent px-6 py-[11px] text-[14px] font-medium leading-6 text-primary ${isBusy ? 'cursor-not-allowed opacity-60' : ''}`, children: state.kind === 'previewing' ? (_jsxs(_Fragment, { children: [_jsx(Spinner, {}), _jsx("span", { children: t('import_csv_previewing', { filename: state.filename }) })] })) : (_jsxs(_Fragment, { children: [_jsx("span", { children: t('import_csv_import_button') }), _jsx(ImportIcon, {})] })) }), _jsx("input", { ref: fileInputRef, type: "file", accept: ".csv,text/csv", onChange: handleFileChange, className: "hidden" })] }), state.kind === 'preview-error' ? (_jsx("p", { className: "m-0 rounded border border-[#ff3e78]/30 bg-[#ff3e78]/5 px-3 py-2 text-[12px] leading-4 text-error", children: state.error.kind === 'bad-format'
                                ? t(state.error.messageKey)
                                : state.error.kind === 'no-valid-rows'
                                    ? t('import_csv_error_no_valid_rows')
                                    : t('import_csv_preview_error', { message: state.error.detail }) })) : state.kind === 'commit-error' ? (_jsx("p", { className: "m-0 rounded border border-[#ff3e78]/30 bg-[#ff3e78]/5 px-3 py-2 text-[12px] leading-4 text-error", children: t('import_csv_commit_error', { message: state.message }) })) : state.kind === 'preview-success' ? (_jsx("p", { className: "m-0 w-full text-center text-[12px] font-semibold leading-normal text-primary", children: t('import_csv_valid_count', { count: state.rows.length }) })) : (_jsx("p", { className: "m-0 w-full text-center text-[12px] leading-normal text-primary-070", children: t('import_csv_template_helper') }))] }), _jsxs("div", { className: "flex w-full items-start gap-2", children: [_jsx(DialogClose, { asChild: true, children: _jsx("button", { type: "button", className: "inline-flex h-10 w-[100px] shrink-0 items-center justify-center rounded border border-primary bg-transparent px-6 py-[11px] text-[14px] font-bold leading-6 text-primary", children: t('common_cancel') }) }), _jsxs("button", { type: "button", onClick: handleCommit, disabled: !canCommit && state.kind !== 'committing', "aria-disabled": !canCommit, className: `inline-flex h-10 flex-1 items-center justify-center gap-2 rounded px-6 py-[11px] text-[14px] font-bold leading-6 text-white ${canCommit || state.kind === 'committing' ? 'bg-primary' : 'cursor-not-allowed bg-inactive-text'}`, children: [state.kind === 'committing' && _jsx(Spinner, {}), _jsx("span", { children: submitLabel })] })] })] }) }));
}
